import type { Client, ClientHeaders } from "@medusajs/js-sdk";

import type { HttpTypes } from "@medusajs/types";

/**
 * An order-history row: a Medusa store order enriched with the fields the
 * custom `/store/order-history` route adds - the supplier (`seller`, via the
 * seller-order link) and the server-computed `fulfillment_status` +
 * `payment_status`. `custom_display_id` is our backend ORDER-module field.
 */
export type StoreOrderHistoryOrder = HttpTypes.StoreOrder & {
  custom_display_id?: string | null;
  seller?: { id: string; name: string; handle?: string } | null;
  fulfillment_status?: string | null;
  payment_status?: string | null;
};

export interface StoreOrderHistoryListParams {
  limit?: number;
  offset?: number;
  /** Sort expression, e.g. `-created_at`. */
  order?: string;
  /** Comma-separated field selection. */
  fields?: string;
  /** Filter by one or more order ids (used for the detail view). */
  id?: string | string[];
  /** Core Medusa order status filter (pending/completed/canceled/...). */
  status?: string | string[];
  /** Supplier filter - resolved server-side through the seller-order link. */
  seller_id?: string | string[];
  /**
   * Computed fulfillment-status filter (one or more canonical states, e.g.
   * `not_fulfilled` / `shipped` / `delivered`). Resolved server-side by
   * re-computing each order's aggregate status, so pagination `count` reflects
   * the filtered set. See salonstocker/api#130.
   */
  fulfillment_status?: string | string[];
  /** Created-at range filter, e.g. `{ $gte, $lte }` (ISO strings). */
  created_at?: { $gte?: string; $lte?: string };
}

export interface StoreOrderHistoryListResponse {
  orders: StoreOrderHistoryOrder[];
  count: number;
  offset: number;
  limit: number;
}

export interface StorePreviousProductIdsParams {
  /**
   * Size of the trailing window, in days. Defaults to 90 server-side; the
   * backend bounds it to 1-365 and 400s anything outside that.
   */
  days?: number;
}

export interface StorePreviousProductIdsResponse {
  /** Distinct product ids, most-recently-ordered first. */
  product_ids: string[];
  /** The number of ids returned. */
  count: number;
  /** The window actually applied, in days. */
  days: number;
}

/**
 * Client for the buyer order-history endpoint (`GET /store/order-history`).
 *
 * This is a dedicated marketplace route (not core `/store/orders`, whose strict
 * query validator rejects custom params like `seller_id`). It returns a flat,
 * one-order-per-seller list scoped to the authenticated customer.
 */
export class OrderHistory {
  private client: Client;

  constructor(client: Client) {
    this.client = client;
  }

  /**
   * Lists the authenticated customer's orders (newest first by default) with
   * the supplier and computed statuses. Supports `seller_id`,
   * `fulfillment_status`, `created_at` range, `status`, sort, and pagination.
   *
   * @example
   * ```ts
   * const { orders, count } = await sdk.store.orderHistory.list({
   *   limit: 10,
   *   seller_id: "sel_123",
   *   created_at: { $gte: "2026-01-01T00:00:00.000Z" }
   * });
   * ```
   */
  list = async (
    query?: StoreOrderHistoryListParams,
    headers?: ClientHeaders
  ): Promise<StoreOrderHistoryListResponse> => {
    return await this.client.fetch<StoreOrderHistoryListResponse>(
      "/store/order-history",
      {
        method: "GET",
        query,
        headers
      }
    );
  };

  /**
   * The distinct product ids this customer ordered in the trailing `days`
   * window, most-recently-ordered first. Marketplace-wide - a consumable bought
   * from two suppliers appears once.
   *
   * Deliberately ids only: the search index already carries titles, prices and
   * availability, so the "Previous orders" quick action turns this straight
   * into a Typesense `filter_by: id:=[...]` rather than paging order history
   * and aggregating client-side.
   *
   * @example
   * ```ts
   * const { product_ids } = await sdk.store.orderHistory.previousProductIds();
   * ```
   */
  previousProductIds = async (
    query?: StorePreviousProductIdsParams,
    headers?: ClientHeaders
  ): Promise<StorePreviousProductIdsResponse> => {
    return await this.client.fetch<StorePreviousProductIdsResponse>(
      "/store/order-history/previous-product-ids",
      {
        method: "GET",
        query,
        headers
      }
    );
  };
}
